import { EXTERNAL_SCOPE_TYPES } from '../models/messaging-user';
import { action } from '@ember/object';
import { decodeText } from '../utils/messaging/format-text';
import { filterSystemAndCurrentUser } from '../utils/messaging/user';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Avatar from './avatar.gjs';
import Component from '@glimmer/component';
import FormatMessagingTimestamp from '../helpers/format-messaging-timestamp';
import styles from './conversation-list-item.module.scss';

export default class ConversationListItem extends Component {
  @service messaging;
  @service messagingNavigation;
  @service intl;

  get isUnread() {
    return this.messaging.currentProfile.unreadConversationIds.includes(this.args.conversation.id);
  }

  get userNames() {
    return this.filteredUsers.map(user => user.name).join(' & ');
  }

  get virtualConversationPlaceholder() {
    return this.intl.t('emc.start_conversation_callout', {
      name: this.userNames,
    });
  }

  get filteredUsers() {
    return filterSystemAndCurrentUser(
      this.args.conversation?.users,
      this.messaging.currentProfile?.user
    );
  }

  get clinicianUser() {
    return this.filteredUsers.find(user => user.externalScope === EXTERNAL_SCOPE_TYPES.user);
  }

  get showAwayIndicator() {
    return this.messaging.currentProfile?.isClient && this.clinicianUser?.isAway;
  }

  @action
  openConversation() {
    this.messagingNavigation.toConversation(this.args.conversation);
  }

  <template>
    <li
      class="{{styles.component}} conversation-list-item"
      data-test-conversation={{@conversation.id}}
      {{on "click" this.openConversation}}
      role="button"
    >
      <Avatar @users={{this.filteredUsers}} />

      <div class="item-details">
        <div class="top-line">
          {{#if this.isUnread}}
            <span
              class="unread-dot"
              aria-label={{t "emc.unread_message_indicator"}}
              data-test-unread-indicator
            ></span>
          {{/if}}
          <span class="user-names" data-test-user-names>
            {{this.userNames}}

            {{#if this.showAwayIndicator}}
              <span class="away-indicator" data-test-away-indicator>
                {{t "emc.away_status.indicator"}}
              </span>
            {{/if}}
          </span>
          <span class="timestamp" data-test-timestamp>
            {{FormatMessagingTimestamp @conversation.updatedAt}}
          </span>
        </div>
        <p class="last-message" data-test-last-message>
          {{#if @conversation.hasMessageDraft}}
            <span class="draft-indicator" data-test-draft-indicator>
              {{t "emc.draft_indicator"}}
            </span>
            {{decodeText @conversation.messageDraft}}
          {{else if @conversation.isNew}}
            <span class="virtual-conversation-callout">
              {{this.virtualConversationPlaceholder}}
            </span>
          {{else}}
            {{decodeText @conversation.lastMessage.text}}
          {{/if}}
        </p>
      </div>
    </li>
  </template>
}
